/**
 * Paged fetching for list queries
 *
 * PostgREST caps every response at the project's `max-rows` setting (1000 on
 * Supabase by default). A query without an explicit `.range()` that matches
 * more rows than the cap returns the first page and NO error — the extra rows
 * are dropped silently. Any list query that can grow past the cap must page
 * through the result instead of issuing a single unbounded request.
 *
 * Usage: pass a callback that REBUILDS the query for each page. The builder
 * must produce a deterministic total order (see `orderStable` note below),
 * otherwise rows can be duplicated or skipped across page boundaries.
 *
 *   const rows = await fetchAllPages<Row>((from, to) =>
 *     supabase.from('bookings').select('*')
 *       .order('created_at', { ascending: false })
 *       .order('id', { ascending: false })   // unique tie-breaker
 *       .range(from, to)
 *   );
 */

/**
 * Rows requested per page. Must be <= the PostgREST `max-rows` setting, else
 * a full page comes back short and paging stops early. 1000 is the Supabase
 * default; lowering `max-rows` below this would require lowering this too.
 */
export const PAGE_SIZE = 1000;

/**
 * Hard stop so a bug in the page builder cannot loop forever. Breaching it
 * THROWS rather than returning what was collected so far: silently returning
 * a truncated list is the exact failure mode this module exists to prevent.
 */
const MAX_PAGES = 50;

/**
 * Fetch every row of a query by walking `.range()` windows until a short page
 * is returned.
 *
 * @param buildPage - Rebuilds the query for a page. Receives inclusive
 *                    `from`/`to` row offsets to pass to `.range(from, to)`.
 * @throws the Supabase error if any page fails, or if MAX_PAGES is exceeded.
 */
export async function fetchAllPages<T>(
  buildPage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const rows: T[] = [];

  for (let page = 0; page < MAX_PAGES; page++) {
    const from = page * PAGE_SIZE;
    const { data, error } = await buildPage(from, from + PAGE_SIZE - 1);

    if (error) throw error;

    const batch = data ?? [];
    for (const row of batch) rows.push(row);

    // A short page means we reached the end of the result set.
    if (batch.length < PAGE_SIZE) return rows;
  }

  throw new Error(
    `fetchAllPages exceeded MAX_PAGES (${MAX_PAGES} x ${PAGE_SIZE} rows). ` +
    'Refusing to return a truncated result — narrow the query or move this ' +
    'list to server-side pagination.'
  );
}

/**
 * Drop duplicate rows by `id`, keeping the first occurrence.
 *
 * Paging is not a snapshot: a row inserted between two page requests shifts
 * every later row down one position, which can surface the same row on both
 * sides of a page boundary. Cheap insurance — apply to any paged result whose
 * sort key is time-based.
 */
export function dedupeById<T extends { id: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

/**
 * Maximum keys to place in a single `.in(...)` filter.
 *
 * PostgREST serialises an `.in()` list into the request URL, so a long list
 * risks the server's URL-length limit. Splitting the list also keeps each
 * request's result set small enough to reason about — though the pages of each
 * chunk still have to be walked, since one chunk alone can match more rows than
 * the row cap. 200 uuids is roughly 7.4 KB of URL, comfortably inside every
 * limit in the chain.
 */
export const IN_CHUNK_SIZE = 200;

/** Split an array into consecutive chunks of at most `size` items. */
export function chunk<T>(items: T[], size: number = IN_CHUNK_SIZE): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Fetch every row matching an `.in(...)` filter over an arbitrarily long key
 * list: the keys are chunked to keep each URL short, and every chunk is paged
 * to completion so no chunk is truncated at the row cap either.
 *
 * @param keys      - Values for the `.in()` filter. An empty list short-circuits.
 * @param buildPage - Rebuilds the query for one chunk and one page window.
 */
export async function fetchAllPagesByChunks<T, K>(
  keys: K[],
  buildPage: (
    keyChunk: K[],
    from: number,
    to: number
  ) => PromiseLike<{ data: T[] | null; error: unknown }>,
  size: number = IN_CHUNK_SIZE
): Promise<T[]> {
  if (keys.length === 0) return [];

  const rows: T[] = [];
  for (const part of chunk(keys, size)) {
    const page = await fetchAllPages<T>((from, to) => buildPage(part, from, to));
    for (const row of page) rows.push(row);
  }
  return rows;
}
